import { gql } from "@apollo/client";
import { FaustTemplate } from "@faustwp/core";
import { EventDetailFragment } from "@/fragments/Event";
import { DetailHero, SEO } from "@components";
import { EventQuery } from "@graphqlTypes";
import { useEffect } from "react";
import { usePageHighlight } from "@/providers/PageHighlightContext";
import { HIGHLIGHT_COLORS } from "@/core/constants";
import { FlexiblePageContentFragment } from "@/fragments/FlexibleContent";
import { FlexibleContentMap } from "@/components/ComponentMaps";
import { getBasicQueryVars, getPageTitle } from "@/core/utils";
import { usePageLoading } from "@/providers/LoadingContext";
import { useTrackRouteChange } from "@/providers/TrackingContext";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import { buildMusicEvent } from "@/core/schema/musicEvent";
import { toAbsoluteProxiedWpUrl } from "@/utils/wpMediaUrl";

const Event: FaustTemplate<EventQuery> = ({ data, loading, pageWrapperData }: FaustPageProps<EventQuery>) => {
  const { setHighlightColor } = usePageHighlight();
  const { recentStories } = pageWrapperData;
  const { previewLoading, setPreviewLoading } = usePageLoading();
  const { siteTitle } = usePageWrapperData();
  const {
    title,
    uri,
    pageData: {
      ogTitle,
      ogDescription,
      featuredImage,
      pageCategory,
    } = {},
    eventTypes,
    detailHero,
    flexiblePageContent,
    featuredEventImage,
    eventData: {
      eventDescription,
      startDate,
      endDate,
      pricingType,
      price,
      ticketLink,
      customCursorText,
      performers
    } = {}
  } = data?.rhEvent || {};
  const isMembersOnly = eventTypes?.nodes.some(type => type.slug === 'members-only');
  const seoTitle = getPageTitle(siteTitle, ogTitle || title);
  const [category] = pageCategory || [];
  const musicEvent = buildMusicEvent({
    name: title ?? "",
    url: `https://rockhall.com${uri ?? ""}`,
    startDate,
    endDate,
    description: eventDescription || ogDescription,
    imageUrl: toAbsoluteProxiedWpUrl(featuredEventImage?.node?.mediaItemUrl || featuredImage?.node?.mediaItemUrl),
    ticketLink,
    performerNames: (performers ?? []).map((p) => p?.performerName),
  });

  useEffect(() => {
    setHighlightColor(HIGHLIGHT_COLORS.BLUE);
  }, [pageCategory]);
  useTrackRouteChange(category?.slug);

  useEffect(() => {
    if (!loading && previewLoading) setPreviewLoading(false);
  }, [loading, previewLoading]);

  if (loading) return;
  return (
    <>
      <SEO title={seoTitle} description={ogDescription} image={featuredImage} jsonLd={musicEvent} />
      <DetailHero
        page={{ node: { ...data.rhEvent, __typename: 'Event' } }}
        headline={title}
        heroLink={ticketLink}
        cursorText={customCursorText}
        isMembersOnly={isMembersOnly}
        startDate={startDate}
        endDate={endDate}
        pricingType={pricingType}
        price={price}
        {...detailHero}
      />
      { flexiblePageContent?.content && (
        <FlexibleContentMap
          content={flexiblePageContent?.content}
          recentStories={recentStories}
        />
      )}
    </>
  );
};

Event.query = gql`
  ${EventDetailFragment}
  ${FlexiblePageContentFragment}
  query Event($databaseId: ID!, $asPreview: Boolean = false, $language: String!) {
    rhEvent(id: $databaseId, asPreview: $asPreview, language: $language) {
      ...EventDetailFragment
      ...PageData
      flexiblePageContent {
        ...ContentFragment
      }
    }
  }
`;

Event.variables = getBasicQueryVars;

export default Event;
